import { Platform, type View } from "@-/com.npmjs.react-native";
import {
  type AnimatedRef,
  measure,
  type SharedValue,
  useAnimatedReaction,
} from "@-/com.npmjs.react-native-reanimated";
import { useLearnMapModeSelector } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { useEffect } from "react";
import { CHINA_MAP_SHEET_HEIGHTS } from "#china-map-sheet-heights";
import { useChinaMapSheetSlideDistance } from "#use-china-map-sheet-slide-distance";

/** The lowest the sheet rests on its own; the learner can drag it lower, to its handle row. */
const LOWEST_RESTING_SHEET = Math.min(...Object.values(CHINA_MAP_SHEET_HEIGHTS));

/**
 * Tells Learn where the top edge of the map's sheet is on screen (`LearnMapMode.sheetTop`), as it
 * slides in and out with the map or is dragged, so the cards' bottom controls, which stay put
 * above the map, hide beneath it as it passes over them. `Infinity` while the sheet is not shown.
 *
 * A sheet dragged below its lowest resting place would uncover those controls over the open map,
 * so it counts as resting there: they stay hidden, and are uncovered from there as the map closes.
 */
export function usePublishLearnMapSheetTop({
  isSheetShown,
  pageRef,
  progress,
  sheetTop,
}: {
  isSheetShown: boolean;
  /** The map page, which the sheet's position is measured from. */
  pageRef: AnimatedRef<View>;
  progress: SharedValue<number>;
  /** The sheet's top edge within the page, before the slide (Gorhom's `animatedPosition`). */
  sheetTop: SharedValue<number>;
}) {
  const learnSheetTop = useLearnMapModeSelector((mode) => mode.sheetTop);
  const perchSheetTop = useLearnMapModeSelector((mode) => mode.perchSheetTop);
  const distance = useChinaMapSheetSlideDistance();
  useAnimatedReaction(
    () => {
      if (Platform.OS === "web") {
        // Web panels have no sheet edge to perch on. Hide card chrome while the map is open.
        return {
          actual: Number.POSITIVE_INFINITY,
          cover: isSheetShown && progress.get() > 0 ? 0 : Number.POSITIVE_INFINITY,
        };
      }
      // Gorhom positions the sheet below the toolbar once it has laid it out; until then the
      // position is still its initial 0.
      if (!isSheetShown || sheetTop.get() <= 0) {
        return { actual: Number.POSITIVE_INFINITY, cover: Number.POSITIVE_INFINITY };
      }
      const page = measure(pageRef);
      if (page === null) {
        return { actual: Number.POSITIVE_INFINITY, cover: Number.POSITIVE_INFINITY };
      }
      const top = Math.min(sheetTop.get(), page.height - LOWEST_RESTING_SHEET);
      const offset = page.pageY + (1 - progress.get()) * distance;
      return { actual: offset + sheetTop.get(), cover: offset + top };
    },
    (top, previous) => {
      if (top.cover !== previous?.cover) {
        learnSheetTop?.set(top.cover);
      }
      if (top.actual !== previous?.actual) {
        perchSheetTop?.set(top.actual);
      }
    },
    [distance, isSheetShown],
  );
  useEffect(
    () => () => {
      learnSheetTop?.set(Number.POSITIVE_INFINITY);
      perchSheetTop?.set(Number.POSITIVE_INFINITY);
    },
    [learnSheetTop, perchSheetTop],
  );
}
